import React, { useState, useEffect, useCallback } from 'react';
import styles from './BudgetPricingModal.module.css';
import { FaMoneyBillWave, FaTimes, FaSave } from 'react-icons/fa';
import { useTheme } from '../../../contexts/ThemeContext';
import { doc, getDoc, updateDoc, collection, addDoc } from 'firebase/firestore';
import { db } from '../../../config/firebaseConfig';
import { toast } from 'react-toastify';

// ProjectCard bileşenini modal dışına çıkar
const ProjectCard = React.memo(({
  project,
  budgetData = {},
  onPricingChange,
  onTitleChange,
  onRemoveVisitType
}) => {
  const currency = project.currency || 'TRY';

  const visitTypes = [
    { key: 'shortVisit', label: 'Short Visit' },
    { key: 'fullVisit', label: 'Full Visit' },
    { key: 'other', label: 'Diğer' }
  ];

  const isVisitTypeSelected = (visitType) => {
    return budgetData && budgetData[visitType] !== undefined;
  };

  return (
    <div className={styles.projectCard}>
      <h3 className={styles.projectTitle}>{project.titleShort || project.titleLong || 'İsimsiz Proje'}</h3>
      <div className={styles.visitTypesGrid}>
        {visitTypes.map(({ key, label }) => (
          <div key={key} className={styles.visitTypeItem}>
            {!isVisitTypeSelected(key) ? (
              <button
                className={styles.addVisitButton}
                onClick={() => onPricingChange(project.id, key, '')}
              >
                <span className={styles.plusIcon}>+</span>
                <span>{label}</span>
              </button>
            ) : (
              <div className={styles.inputGroup}>
                <div className={styles.labelWithRemove}>
                  <label className={styles.inputLabel}>
                    {key === 'other' && budgetData[`${key}Title`]
                      ? budgetData[`${key}Title`]
                      : label}
                  </label>
                  <button
                    className={styles.removeButton}
                    onClick={() => onRemoveVisitType(project.id, key)}
                    title="Kaldır"
                  >
                    <FaTimes />
                  </button>
                </div>

                {/* Eğer "Diğer" seçiliyse başlık inputu göster */}
                {key === 'other' && (
                  <div className={styles.titleInput}>
                    <input
                      type="text"
                      value={budgetData[`${key}Title`] || ''}
                      onChange={(e) => onTitleChange(project.id, key, e.target.value)}
                      placeholder="Başlık giriniz (örn: Telefon Görüşmesi)"
                      className={styles.textInput}
                    />
                  </div>
                )}

                <div className={styles.inputWithCurrency}>
                  <input
                    type="number"
                    min="0"
                    max="999999"
                    step="0.01"
                    value={budgetData[key] || ''}
                    onChange={(e) => onPricingChange(project.id, key, e.target.value)}
                    placeholder="0.00"
                    className={styles.priceInput}
                  />
                  <span className={styles.currency}>{currency}</span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});

ProjectCard.displayName = 'ProjectCard';

const BudgetPricingModal = ({ selectedEmployee, onClose, show }) => {
  const { isDarkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [projects, setProjects] = useState([]);
  const [budgetPricing, setBudgetPricing] = useState({});

  useEffect(() => {
    if (show && selectedEmployee) {
      fetchEmployeeData();
    }
  }, [show, selectedEmployee]);

  const fetchEmployeeData = async () => {
    if (!selectedEmployee || !selectedEmployee.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Çalışan bilgilerini Firestore'dan çek
      const employeeRef = doc(db, 'Employees', selectedEmployee.id);
      const employeeDoc = await getDoc(employeeRef);

      if (!employeeDoc.exists()) {
        toast.error('Çalışan bilgileri bulunamadı');
        setLoading(false);
        return;
      }

      const employeeData = employeeDoc.data();

      // Çalışanın projelerini al
      const employeeProjects = employeeData.projects || [];

      if (employeeProjects.length === 0) {
        setProjects([]);
        setLoading(false);
        return;
      }

      // Proje bilgilerini set et
      setProjects(employeeProjects);

      // Mevcut bütçe ücretlendirme verilerini yükle (sonraki görevde implement edilecek)
      const existingBudgetPricing = employeeData.budgetPricing || {};
      setBudgetPricing(existingBudgetPricing);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching employee data:', error);
      toast.error('Çalışan bilgileri yüklenirken bir hata oluştu');
      setLoading(false);
    }
  };

  const handlePricingChange = useCallback((projectId, visitType, value) => {
    // Boş string'i kabul et (kullanıcı silme yapabilir)
    if (value === '') {
      setBudgetPricing(prev => ({
        ...prev,
        [projectId]: {
          ...prev[projectId],
          [visitType]: ''
        }
      }));
      return;
    }

    const numValue = parseFloat(value);

    // Negatif değerleri engelle
    if (numValue < 0) return;

    // Maksimum değer kontrolü
    if (numValue > 999999) return;

    setBudgetPricing(prev => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        [visitType]: value // String olarak sakla, input kontrolü için
      }
    }));
  }, []);

  const handleTitleChange = useCallback((projectId, visitType, title) => {
    setBudgetPricing(prev => ({
      ...prev,
      [projectId]: {
        ...prev[projectId],
        [`${visitType}Title`]: title
      }
    }));
  }, []);



  const handleRemoveVisitType = useCallback((projectId, visitType) => {
    setBudgetPricing(prev => {
      const projectData = { ...prev[projectId] };
      delete projectData[visitType];
      // Başlık varsa onu da sil
      delete projectData[`${visitType}Title`];
      return {
        ...prev,
        [projectId]: projectData
      };
    });
  }, []);



  const handleSave = async () => {
    if (!selectedEmployee || !selectedEmployee.id) {
      toast.error('Çalışan bilgisi bulunamadı');
      return;
    }

    try {
      setSaving(true);

      // Timestamp ve updatedBy bilgilerini ekle
      const updatedBudgetPricing = {};
      const budgetExpensesToCreate = []; // Oluşturulacak gider kayıtları

      Object.keys(budgetPricing).forEach(projectId => {
        const projectBudget = budgetPricing[projectId];

        updatedBudgetPricing[projectId] = {
          ...projectBudget,
          updatedAt: new Date(),
          updatedBy: 'admin' // TODO: Gerçek kullanıcı bilgisi eklenebilir
        };

        // Her ziyaret tipi için gider kaydı oluştur
        const visitTypes = [
          { key: 'shortVisit', label: 'Short Visit' },
          { key: 'fullVisit', label: 'Full Visit' },
          { key: 'other', label: projectBudget.otherTitle || 'Diğer' }
        ];

        visitTypes.forEach(({ key, label }) => {
          const amount = projectBudget[key];

          // Eğer bu ziyaret tipi için değer girildiyse
          if (amount && parseFloat(amount) > 0) {
            budgetExpensesToCreate.push({
              projectId: projectId,
              BütçeAdı: label,
              BütçeKaynak: 'Çalışan Bütçe Ücretlendirme',
              BütçeSahibi: `${selectedEmployee.eName} ${selectedEmployee.eSurname}`,
              BütçeTipi: 'Gider',
              BütçeKategorisi: 'Personel',
              GiderTipi: 'Personel Gideri',
              ParaBirimi: 'TRY', // TODO: Proje para birimini kullan
              KDV: '0',
              BütçeMiktarı: parseFloat(amount),
              BütçeTarihi: new Date().toISOString().split('T')[0],
              BeklenenÖdemeTarihi: '',
              ÖdemeDurumu: 'Beklemede',
              AktifDurum: 'Aktif',
              createdAt: new Date().toISOString(),
              createdBy: 'Çalışan Bütçe Ücretlendirme Modülü'
            });
          }
        });
      });

      // Firestore'a çalışan bilgilerini kaydet
      const employeeRef = doc(db, 'Employees', selectedEmployee.id);
      await updateDoc(employeeRef, {
        budgetPricing: updatedBudgetPricing
      });

      // Budgets koleksiyonuna gider kayıtlarını ekle
      let addedExpensesCount = 0;
      for (const expense of budgetExpensesToCreate) {
        await addDoc(collection(db, 'Budgets'), expense);
        addedExpensesCount++;
      }

      if (addedExpensesCount > 0) {
        toast.success(`Bütçe ücretlendirme bilgileri kaydedildi ve ${addedExpensesCount} gider kaydı oluşturuldu`);
      } else {
        toast.success('Bütçe ücretlendirme bilgileri başarıyla kaydedildi');
      }

      setSaving(false);
      onClose();
    } catch (error) {
      console.error('Error saving budget pricing:', error);
      toast.error('Kaydetme sırasında bir hata oluştu');
      setSaving(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!show) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleOverlayClick}>
      <div className={`${styles.modalContent} ${isDarkMode ? 'dark-mode' : ''}`}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FaMoneyBillWave className={styles.modalIcon} />
            Çalışan Bütçe Ücretlendirme - {selectedEmployee?.eName} {selectedEmployee?.eSurname}
          </h2>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Kapat"
          >
            <FaTimes />
          </button>
        </div>

        <div className={styles.modalBody}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.spinner}></div>
              <p>Yükleniyor...</p>
            </div>
          ) : projects.length === 0 ? (
            <div className={styles.emptyState}>
              <FaMoneyBillWave className={styles.emptyIcon} />
              <p>Bu çalışanın atandığı proje bulunmamaktadır</p>
            </div>
          ) : (
            <div className={styles.projectsList}>
              {projects.map(project => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  budgetData={budgetPricing[project.id]}
                  onPricingChange={handlePricingChange}
                  onTitleChange={handleTitleChange}
                  onRemoveVisitType={handleRemoveVisitType}
                />
              ))}
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button
            className={styles.cancelButton}
            onClick={onClose}
            disabled={saving}
          >
            İptal
          </button>
          <button
            className={styles.saveButton}
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? (
              <>
                <div className={styles.buttonSpinner}></div>
                Kaydediliyor...
              </>
            ) : (
              <>
                <FaSave className={styles.buttonIcon} />
                Kaydet
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BudgetPricingModal;
